import React, { useMemo } from "react";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import type { ReviewFalProps } from "@features/vessel-call/vessel-call-status/VesselCallDetails/ImoFalReview/types";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useTranslation } from "react-i18next";
import { getFal1GeneralDeclarationData } from "../utils";

const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3";
const wrapperFirstStyleClass = "pt-3";
const transStyleClass = "font-normal text-[#646A77]";

const ReviewFal1: React.FC<ReviewFalProps> = ({ vesselCallDetails }) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const {
    translationTitle,
    testIds,
    fieldsLeft,
    fieldsRight,
    signedDocUrl,
    signedDocName,
    signedTitle,
  } = useMemo(
    () =>
      getFal1GeneralDeclarationData({
        vesselCallDetails,
        t,
      }),
    [vesselCallDetails, t],
  );

  return (
    <div className="flex flex-col gap-4" data-testid={testIds.wrapper}>
      <h2 className="text-xl">{translationTitle}</h2>
      <Paper variant="outlined">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid={testIds.grid}>
          <div data-testid={testIds.left}>
            {fieldsLeft.map((f, idx) => (
              <PaperItem
                key={f.testId}
                testId={f.testId}
                translate={f.translate}
                textStyles="font-semibold"
                tranStyle={transStyleClass}
                wrapperStyle={idx === 0 ? wrapperFirstStyleClass : wrapperStyleClass}
                text={f.value}
              />
            ))}
          </div>
          <div data-testid={testIds.right}>
            {fieldsRight.map((f, idx) => (
              <PaperItem
                key={f.testId}
                testId={f.testId}
                translate={f.translate}
                textStyles="font-semibold"
                tranStyle={transStyleClass}
                wrapperStyle={idx === 0 ? wrapperFirstStyleClass : wrapperStyleClass}
                text={f.value}
              />
            ))}
          </div>
        </div>
      </Paper>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          {signedDocUrl ? (
            <DocumentCard
              testId={testIds.signedCard}
              title={signedTitle}
              fileName={signedDocName}
              onDownload={() =>
                handleDownload("certificate", "bff-vessel-call", signedDocUrl, signedDocName)
              }
              onPreview={() => handlePreview("certificate", "bff-vessel-call", signedDocUrl)}
            />
          ) : (
            <Paper variant="outlined" data-testid={testIds.signedEmpty}>
              <p className="text-center text-gray-500 font-normal text-sm">
                {t("vessel-call.certificates.empty")}
              </p>
            </Paper>
          )}
        </div>
        <div className="hidden md:block" />
      </div>
    </div>
  );
};

export default ReviewFal1;
